import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { Field, FieldLabel } from "@/shared/components/ui/field";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { FieldErrors } from "@/shared/components/field/field";
import { useFieldErrors } from "@/shared/lib/errors/err-validation-failed-context";
import { cn } from "@/shared/lib/utils";
import { useOmniDataSheetListQuery } from "../api/data-sheet-api";

/**
 * DataSheetsField selects the data sheets of the company and service kind
 * a position or option references, in a searchable select allowing
 * several. It offers the active data sheets and keeps the selected ones,
 * also when inactive. It shows and resets the errors of fieldName.
 */
export function DataSheetsField({
  companyId,
  serviceKind,
  value,
  onValueChange,
  fieldName,
  disabled,
}: {
  companyId: string;
  serviceKind: string;
  value: string[];
  onValueChange: (ids: string[]) => void;
  fieldName: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const { errors, invalid, reset } = useFieldErrors(fieldName);
  const { data } = useOmniDataSheetListQuery(
    {
      company: { active: true, values: [companyId] },
      serviceKind: { active: true, values: [serviceKind] },
    },
    { skip: !companyId },
  );
  const sheets = data?.items ?? [];
  const offered = sheets.filter((d) => d.active || value.includes(d.id));
  const selectedLabels = value.map(
    (id) => sheets.find((d) => d.id === id)?.label ?? t("Unbekannt"),
  );

  const toggle = (id: string) => {
    reset();
    onValueChange(
      value.includes(id)
        ? value.filter((selected) => selected !== id)
        : [...value, id],
    );
  };

  return (
    <Field data-invalid={invalid || undefined} className="sm:col-span-2">
      <FieldLabel>{t("Datenblätter")}</FieldLabel>
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="justify-between font-normal"
            disabled={disabled}
            aria-invalid={invalid || undefined}
          >
            {selectedLabels.length > 0 ? (
              <span className="truncate">{selectedLabels.join(", ")}</span>
            ) : (
              <span className="text-muted-foreground">
                {t("Datenblätter auswählen ...")}
              </span>
            )}
            <ChevronsUpDown className="opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0" align="start">
          <Command>
            <CommandInput placeholder={t("Suchen")} className="h-9" />
            <CommandList>
              <CommandEmpty>{t("Keine Datenblätter gefunden")}</CommandEmpty>
              <CommandGroup>
                {offered.map((d) => (
                  <CommandItem
                    key={d.id}
                    // the label is searched, the id keeps equal labels apart
                    value={`${d.label} ${d.id}`}
                    onSelect={() => toggle(d.id)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value.includes(d.id) ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span>{d.label}</span>
                    {!d.active && (
                      <span className="text-muted-foreground ml-1">
                        ({t("inaktiv")})
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <FieldErrors errors={errors} />
    </Field>
  );
}
